import React, { useState, useEffect } from "react";
import {
    Box as MuiBox,
    List as MuiList,
    ListItem as MuiListItem,
    ListItemButton as MuiListItemButton,
    Typography as MuiTypography,
    Skeleton as MuiSkeleton,
    Card as MuiCard,
    Button as MuiButton
} from "@mui/material";
import { History as HistoryIcon } from "@mui/icons-material";

import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "@/state/store";
import { WorkproductDocument } from "@/api";
import { setActiveTab as setActiveTabRedux, selectActiveTabIndex, selectActiveTabName, selectActiveTabSectionId } from "@/state/activeEditor/activeEditorSlice";
import { getSectionIcon } from "./sectionIcons";
import { dedupePeersByUserAndTarget, usePresence } from "@/collab/usePresence";

interface DocumentSectionsListProps {
    workProductId: number | string;
    onSectionChange?: (sectionIndex: number, section: WorkproductDocument) => void;
    readOnly?: boolean;
}

// Flip on once the backend reports per-section completion state.
const SHOW_SECTION_STATE_DOT = false;

// Light-mode design tokens — keep in sync with intelligenic-tokens.css.
const T = {
    bgPaper: "#ffffff",
    divider: "rgba(0, 0, 0, 0.07)",
    border: "rgba(0, 0, 0, 0.07)",
    borderStrong: "rgba(0, 0, 0, 0.1)",
    fg1: "#111827",
    fg2: "#374151",
    fg3: "#4b5563",
    primary: "#94b2ff",
    primarySoft: "rgba(148, 178, 255, 0.07)",
    success: "#059669",
    warning: "#d97706",
    shadow1: "0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 1px rgba(15, 23, 42, 0.02)"
};

// Once the backend supports it, doc.state should be 'done' | 'doing' | 'todo'.
type SectionState = "done" | "doing" | "todo";
const dotForState = (state: SectionState) => {
    if (state === "done") return { bgcolor: T.success, border: "none" };
    if (state === "doing") return { bgcolor: T.warning, border: "none" };
    return { bgcolor: "transparent", border: `1px solid ${T.borderStrong}` };
};


const DocumentSectionsList: React.FC<DocumentSectionsListProps> = ({ workProductId, onSectionChange, readOnly = true }) => {
    const dispatch = useDispatch<AppDispatch>();
    const [selectedSectionIndex, setSelectedSectionIndex] = useState<number>(0);
    const [currentWorkProductId, setCurrentWorkProductId] = useState<number | string | null>(null);

    // Check if work product ID is valid
    const isValidWorkProductId = React.useMemo(() => {
        const id = typeof workProductId === "string" ? parseInt(workProductId, 10) : workProductId;
        return !isNaN(id) && id > 0;
    }, [workProductId]);

    // Reset state when work product changes
    useEffect(() => {
        if (workProductId !== currentWorkProductId) {
            console.log("DocumentSectionsList: Work product changed, resetting to first section", {
                oldId: currentWorkProductId,
                newId: workProductId,
                isValid: isValidWorkProductId
            });
            setSelectedSectionIndex(0);
            setCurrentWorkProductId(workProductId);
        }
    }, [workProductId, currentWorkProductId, isValidWorkProductId]);

    // Get documents from Redux
    const { status, documents, multipleVersions, activeVersion } = useSelector((state: RootState) => state.workProductDocuments);
    const activeTabIndexFromRedux = useSelector(selectActiveTabIndex);
    const activeTabNameFromRedux = useSelector(selectActiveTabName);
    const activeTabSectionIdFromRedux = useSelector(selectActiveTabSectionId);

    // Remote collaborators per section (empty when collab is inactive).
    const peers = dedupePeersByUserAndTarget(usePresence());

    // Filter out content_only sections and sort documents by sort_order
    // content_only sections are used by the processor to build work products and should not be shown in navigation
    const sortedDocuments = React.useMemo(() => {
        if (!documents || documents.length === 0) return [];
        return [...documents]
            .filter(doc => !doc.content_only) // Exclude content_only sections from navigation
            .sort((a, b) => a.sort_order - b.sort_order);
    }, [documents]);

    // Fire an initial onSectionChange so parents (e.g. the title bar breadcrumb) can
    // surface the active section name before the user clicks anything.
    useEffect(() => {
        if (!onSectionChange) return;
        if (!sortedDocuments || sortedDocuments.length === 0) return;
        const initial = sortedDocuments[selectedSectionIndex] || sortedDocuments[0];
        if (initial) onSectionChange(selectedSectionIndex, initial);
        // Only when the section list itself changes — selection changes already notify via handleSectionClick.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortedDocuments]);

    // Sync with Redux state changes (e.g., from chat)
    useEffect(() => {
        if (!sortedDocuments || sortedDocuments.length === 0) return;

        // Check if this is a name-based switch (index = -1)
        if (activeTabIndexFromRedux === -1 && activeTabNameFromRedux) {
            console.log(`Name-based section switch requested for: ${activeTabNameFromRedux}`);

            // Find by section ID first (most reliable)
            let targetIndex = -1;
            if (activeTabSectionIdFromRedux) {
                targetIndex = sortedDocuments.findIndex(doc => String(doc.section_id) === activeTabSectionIdFromRedux);
            }

            // Fallback to name matching if section ID not found
            if (targetIndex === -1) {
                targetIndex = sortedDocuments.findIndex(doc => doc.section_title.toLowerCase() === activeTabNameFromRedux.toLowerCase());
            }

            if (targetIndex !== -1 && targetIndex !== selectedSectionIndex) {
                console.log(`Found section at index ${targetIndex} for: ${activeTabNameFromRedux}`);
                handleSectionClick(targetIndex, sortedDocuments[targetIndex]);
            } else if (targetIndex === -1) {
                console.warn(`Could not find section for: ${activeTabNameFromRedux}`);
            }
        } else if (activeTabIndexFromRedux >= 0 && activeTabIndexFromRedux !== selectedSectionIndex && sortedDocuments[activeTabIndexFromRedux]) {
            // Normal index-based sync
            handleSectionClick(activeTabIndexFromRedux, sortedDocuments[activeTabIndexFromRedux]);
        }
    }, [activeTabIndexFromRedux, activeTabNameFromRedux, activeTabSectionIdFromRedux, sortedDocuments]);

    const handleSectionClick = (index: number, section: WorkproductDocument) => {
        setSelectedSectionIndex(index);

        // Update Redux state
        dispatch(
            setActiveTabRedux({
                index: index,
                name: section.section_title,
                sectionId: String(section.section_id)
            })
        );

        // Notify parent component
        if (onSectionChange) {
            onSectionChange(index, section);
        }
    };

    const handleRevertToPreviousVersion = () => {
        console.log("Reverting to previous version from version:", activeVersion);
        // TODO: Implement revert logic
    };

    // Invalid work product ID state
    if (!isValidWorkProductId) {
        return (
            <MuiBox sx={{ p: 3, textAlign: "center" }}>
                <MuiTypography variant="body2" color="text.secondary">
                    Work product is not yet saved
                </MuiTypography>
                <MuiTypography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    Generate or activate this work product to view sections
                </MuiTypography>
            </MuiBox>
        );
    }

    // Loading state
    if (status === "loading") {
        return (
            <MuiBox sx={{ p: 2 }}>
                {Array.from({ length: 5 }).map((_, index) => (
                    <MuiSkeleton key={index} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
                ))}
            </MuiBox>
        );
    }

    // Error state - only show if we have a valid ID but fetch failed
    if (status === "failed") {
        return (
            <MuiBox sx={{ p: 3, textAlign: "center" }}>
                <MuiTypography variant="body2" color="error">
                    Failed to load document sections
                </MuiTypography>
                <MuiTypography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    Please try refreshing or contact support
                </MuiTypography>
            </MuiBox>
        );
    }

    // Empty state
    if (!sortedDocuments || sortedDocuments.length === 0) {
        return (
            <MuiBox sx={{ p: 3, textAlign: "center" }}>
                <MuiTypography variant="body2" color="text.secondary">
                    No sections available
                </MuiTypography>
            </MuiBox>
        );
    }

    return (
        <MuiBox sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", gap: "5px", p: "14px 10px" }}>
            {multipleVersions && !readOnly && (
                <MuiButton
                    variant="outlined"
                    color="warning"
                    fullWidth
                    startIcon={<HistoryIcon />}
                    onClick={handleRevertToPreviousVersion}
                    sx={{
                        mb: 1,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.813rem",
                        borderWidth: 2,
                        py: 0.75,
                        "&:hover": {
                            borderWidth: 2,
                            backgroundColor: "warning.main",
                            color: "warning.contrastText"
                        }
                    }}
                >
                    Revert To Previous Version
                </MuiButton>
            )}

            {sortedDocuments.map((section, index) => {
                const isSelected = selectedSectionIndex === index;
                // Placeholder until backend reports section state. Rendered when SHOW_SECTION_STATE_DOT is on.
                const sectionState: SectionState = "todo";
                const dotStyle = dotForState(sectionState);
                const sectionPeers = peers.filter(
                    peer => peer.activity?.target.kind === "section" && peer.activity.target.id === String(section.warehouse_id)
                );
                return (
                    <MuiCard
                        key={section.section_id}
                        onClick={() => handleSectionClick(index, section)}
                        data-selected={isSelected}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            px: "12px",
                            py: "10px",
                            cursor: "pointer",
                            transition: "background .12s, color .12s, border-color .12s, box-shadow .12s",
                            backgroundColor: isSelected ? T.primarySoft : T.bgPaper,
                            color: isSelected ? T.primary : T.fg2,
                            border: `1px solid ${isSelected ? T.primary : T.divider}`,
                            borderRadius: "8px",
                            boxShadow: "none",
                            "&:hover": !isSelected
                                ? {
                                      backgroundColor: T.bgPaper,
                                      borderColor: T.divider,
                                      boxShadow: T.shadow1
                                  }
                                : undefined,
                            "&:focus-visible": {
                                outline: `2px solid ${T.primary}`,
                                outlineOffset: 2
                            }
                        }}
                    >
                        <MuiBox
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: isSelected ? T.primary : T.fg3,
                                flexShrink: 0,
                                "& svg": { width: 16, height: 16 }
                            }}
                        >
                            {getSectionIcon(section.section_title)}
                        </MuiBox>

                        <MuiTypography
                            variant="body2"
                            sx={{
                                flex: 1,
                                fontWeight: isSelected ? 600 : 500,
                                fontSize: 13,
                                lineHeight: 1.2,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: "inherit"
                            }}
                            title={section.section_title}
                        >
                            {section.section_title}
                        </MuiTypography>

                        {sectionPeers.length > 0 && (
                            <MuiBox sx={{ display: "flex", flexShrink: 0 }}>
                                {sectionPeers.slice(0, 3).map((peer, i) => (
                                    <MuiBox
                                        key={`${peer.user!.userid}:${peer.activity!.target.id}`}
                                        title={`${peer.user!.name} is ${peer.activity!.activity}`}
                                        aria-label={`${peer.user!.name} is ${peer.activity!.activity}`}
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "4px",
                                            bgcolor: peer.user!.color,
                                            border: "1px solid #ffffff",
                                            ml: i ? "-3px" : 0
                                        }}
                                    />
                                ))}
                            </MuiBox>
                        )}

                        {SHOW_SECTION_STATE_DOT && (
                            <MuiBox
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: "3px",
                                    flexShrink: 0,
                                    bgcolor: dotStyle.bgcolor,
                                    border: dotStyle.border
                                }}
                            />
                        )}
                    </MuiCard>
                );
            })}
        </MuiBox>
    );
};

export default DocumentSectionsList;
